import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Radio, Typography, Tooltip } from 'antd';
import MapView from '../MapView';
import { TIMELINE_COLOR, TIMELINE_SEGMENT } from './constants';
import { formatCoordinates, formatDuration, formatPercent, formatTimestamp } from './format';

const { Title, Text } = Typography;

/**
 * Why the timeline is empty — three different reasons that must not share one message.
 *
 * The backend returns an empty `gps_health_timeline` for all three, so the payload alone
 * cannot tell them apart. "Not started" must come from the trip's own lifecycle status
 * (`notStarted`, driven by `trip_status_id`), not from whether a duration happened to
 * compute — `totalSeconds` can read as 0 on an already-started, already-closed trip
 * whenever the reconciliation summary or tracking timings are momentarily missing, and
 * that gap must never be allowed to claim the trip "has not started" once it demonstrably
 * has (or has already ended).
 */
const emptyTimelineReason = ({ hasReconciliation, notStarted }) => {
	if (!hasReconciliation) {
		return {
			title: 'GPS coverage has not been measured for this trip.',
			body: 'No reconciliation is available for it, so there is no health timeline to draw. What is known today is shown below — use Map View for the trail itself.',
		};
	}
	if (notStarted) {
		return {
			title: 'Tracking has not started for this trip.',
			body: 'GPS health is measured against the trip window, which opens when the trip starts and the driver checks in. There is nothing to measure against yet.',
		};
	}
	return {
		title: 'No GPS positions were recorded during this trip.',
		body: 'The trip ran its full duration without a single source reporting a position, so coverage is 0%. Map View will be empty too.',
	};
};

/**
 * What the GPS did *during* the trip, replacing "Current GPS Status: Offline" — which
 * answers a question nobody is asking by the time they open a closed trip.
 *
 * Timeline / Map View are two readings of the same trail: when the signal dropped, and
 * where it was when it did. The map is the existing `MapView`, unchanged.
 *
 * Three distinct states, and conflating any two would be a lie:
 *   - vehicle has no GPS hardware  → there was never a signal to lose
 *   - coverage not measured yet    → we do not know (pre-reconciliation-API)
 *   - measured                     → the bar, green tracked, red gaps
 */
function GpsHealthTimeline({ model, tripId, trackingNodes, trackingInfo }) {
	const [view, setView] = useState('timeline');
	const { gps, window: tripWindow } = model;

	const spansDays = (tripWindow.totalSeconds || 0) > 86400;
	const totalSeconds = tripWindow.totalSeconds || 0;
	const segments = gps.segments || [];
	const hasTimeline = segments.length > 0 && totalSeconds > 0;
	const noHardware = gps.installed === false;
	const emptyReason = emptyTimelineReason({
		hasReconciliation: model.hasReconciliation,
		notStarted: model.notStarted,
	});

	return (
		<Card
			bodyStyle={{ padding: '24px 28px' }}
			style={{
				borderRadius: 14,
				border: 'none',
				boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
				marginTop: 20,
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 16,
					marginBottom: 20,
				}}
			>
				<Title level={5} style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>
					GPS Health
				</Title>
				{!noHardware && (
					<Radio.Group
						value={view}
						onChange={(e) => setView(e.target.value)}
						buttonStyle="solid"
						size="small"
					>
						<Radio.Button value="timeline">Timeline</Radio.Button>
						<Radio.Button value="map">Map View</Radio.Button>
					</Radio.Group>
				)}
			</div>

			{noHardware && (
				<Text style={{ fontSize: 13, color: '#64748b' }}>
					This vehicle has no GPS hardware, so odometer readings are the only distance
					source. There is no GPS timeline to show.
				</Text>
			)}

			{!noHardware && view === 'map' && (
				<div style={{ height: 420, borderRadius: 10, overflow: 'hidden' }}>
					<MapView
						tripId={tripId}
						trackingInfo={trackingNodes}
						liveTracking={trackingInfo}
						tripInfo={trackingInfo?.trip_info}
					/>
				</div>
			)}

			{!noHardware && view === 'timeline' && hasTimeline && (
				<>
					<div
						style={{ display: 'flex', height: 40, borderRadius: 8, overflow: 'hidden' }}
					>
						{segments.map((segment) => {
							const seconds = segment.durationSeconds || 0;
							const isGap = segment.type === TIMELINE_SEGMENT.GAP;
							const share = (seconds / totalSeconds) * 100;
							return (
								<Tooltip
									key={segment.key}
									title={
										<div style={{ fontSize: 12 }}>
											<div style={{ fontWeight: 600 }}>
												{isGap ? 'GPS gap' : 'GPS active'} ·{' '}
												{formatDuration(seconds)}
											</div>
											<div>
												{formatTimestamp(segment.start, spansDays)} →{' '}
												{formatTimestamp(segment.end, spansDays)}
											</div>
											{isGap && segment.lastFix && (
												<div style={{ marginTop: 4 }}>
													Last fix before drop:{' '}
													{formatCoordinates(
														segment.lastFix.lat,
														segment.lastFix.lng
													)}
													{segment.lastFix.source
														? ` (${segment.lastFix.source})`
														: ''}
												</div>
											)}
											{isGap && !segment.lastFix && (
												<div style={{ marginTop: 4 }}>
													No fix recorded before this gap.
												</div>
											)}
										</div>
									}
								>
									<div
										style={{
											// A short outage must stay visible and hoverable, so
											// every segment gets a floor of 3% of the bar.
											flex: `0 0 ${Math.max(share, 3)}%`,
											background: isGap
												? TIMELINE_COLOR[TIMELINE_SEGMENT.GAP]
												: TIMELINE_COLOR[TIMELINE_SEGMENT.ACTIVE],
											color: '#ffffff',
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: '0.6px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											overflow: 'hidden',
											whiteSpace: 'nowrap',
										}}
									>
										{/* Below ~8% the label is unreadable; the tooltip carries it instead. */}
										{share >= 8 &&
											(isGap
												? `GAP · ${formatDuration(seconds).toUpperCase()}`
												: 'ACTIVE')}
									</div>
								</Tooltip>
							);
						})}
					</div>

					<div
						style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}
					>
						<Text style={{ fontSize: 13, color: '#64748b' }}>
							{formatTimestamp(tripWindow.start?.toISOString(), spansDays)}
						</Text>
						<Text style={{ fontSize: 13, color: '#64748b' }}>
							{tripWindow.endIsNow
								? 'now'
								: formatTimestamp(tripWindow.end?.toISOString(), spansDays)}
						</Text>
					</div>

					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 20,
							marginTop: 14,
							flexWrap: 'wrap',
						}}
					>
						<Text style={{ fontSize: 13, color: '#64748b' }}>
							<span
								style={{
									display: 'inline-block',
									width: 9,
									height: 9,
									borderRadius: '50%',
									background: TIMELINE_COLOR[TIMELINE_SEGMENT.ACTIVE],
									marginRight: 7,
								}}
							/>
							<span>GPS Active</span>
						</Text>
						<Text style={{ fontSize: 13, color: '#64748b' }}>
							<span
								style={{
									display: 'inline-block',
									width: 9,
									height: 9,
									borderRadius: '50%',
									background: TIMELINE_COLOR[TIMELINE_SEGMENT.GAP],
									marginRight: 7,
								}}
							/>
							<span>GPS Gap — hover a segment for detail</span>
						</Text>
						<Text style={{ fontSize: 13, color: '#334155', marginLeft: 'auto' }}>
							Active {formatDuration(gps.coverageSeconds)} of{' '}
							{formatDuration(totalSeconds)} ({formatPercent(gps.coveragePercent)})
						</Text>
					</div>
				</>
			)}

			{!noHardware && view === 'timeline' && !hasTimeline && (
				<div
					style={{
						background: '#f8fafc',
						border: '1px dashed #e2e8f0',
						borderRadius: 10,
						padding: '20px 22px',
					}}
				>
					<Text style={{ fontSize: 13, color: '#475569' }}>{emptyReason.title}</Text>
					<Text style={{ display: 'block', fontSize: 12, color: '#94a3b8' }}>
						{emptyReason.body}
					</Text>
					<div style={{ marginTop: 16, display: 'flex', gap: 40, flexWrap: 'wrap' }}>
						<div>
							<Text style={{ fontSize: 12, color: '#94a3b8' }}>
								GPS status right now
							</Text>
							<Text
								strong
								style={{
									display: 'block',
									fontSize: 15,
									color:
										gps.liveStatus?.toLowerCase() === 'online'
											? '#16a34a'
											: '#dc2626',
								}}
							>
								{gps.liveStatus || '—'}
							</Text>
						</div>
						<div>
							<Text style={{ fontSize: 12, color: '#94a3b8' }}>
								Last location ping
							</Text>
							<Text strong style={{ display: 'block', fontSize: 15 }}>
								{gps.lastPingDisplay || '—'}
							</Text>
						</div>
					</div>
				</div>
			)}
		</Card>
	);
}

GpsHealthTimeline.propTypes = {
	model: PropTypes.object.isRequired,
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	trackingNodes: PropTypes.array,
	trackingInfo: PropTypes.object,
};

export default GpsHealthTimeline;
